import React from 'react';
import { formatMoney } from '@belong/common';
import {
  Text,
  LinkBase,
  Link,
  Button,
  Tooltip,
  ConditionalWrap,
} from '@belong/ui';
import type { Href } from '@belong/ui';
import cx from 'clsx';
import BulletList from 'components/BulletList/BulletList';
import UnitBasicInfo from 'models/common/UnitBasicInfo';
import { BASE_PATHS } from 'routes/paths';
import HomeInfo from '../../components/display/Home/HomeInfo/HomeInfo';
import styles from './TourAndAppsCard.module.css';
import css from './tour-card.module.css';

export const TourCardTitleWithDetails = ({
  title,
  basicInfo,
  rentAmount,
}: {
  title: string;
  basicInfo: UnitBasicInfo;
  rentAmount: number;
}) => {
  return (
    <>
      <Text
        variant="h3"
        fontWeight="semibold"
        className={cx('mt-xs md:truncate', css.title)}
      >
        {title}
      </Text>
      <div className="text-p1 font-regular">
        <BulletList
          small
          flexWrap
          listItems={[
            <HomeInfo key="0" unitBasic={basicInfo} />,
            <span key="1">
              {formatMoney(rentAmount)}
              /m
            </span>,
          ]}
        />
      </div>
    </>
  );
};

type TourCardAction = {
  // Optional: an action that supplies `customContent` renders that instead of a
  // button or link, and carries no label — which is what the sole caller builds
  // for its main CTA.
  label?: string;
  href?: Href;
  onClick?: () => void;
  tooltipContent?: React.ReactNode;
  isCTA?: boolean;
  /** Use this prop if you want to render custom content instead of a button or link */
  customContent?: React.ReactNode;
};

export const TourCardActions = ({
  actions,
}: {
  // Required, because it is mapped over unguarded below; both call sites pass one.
  actions: TourCardAction[];
}) => (
  <div className="mt-sm flex flex-col-reverse items-end gap-lg px-2sm pb-sm text-center md:flex-row md:items-center md:justify-end md:gap-sm md:p-0">
    {actions.map((action) => {
      if (action.customContent) {
        return action.customContent;
      }

      const commonProps = {
        key: action.label,
        variant: action.isCTA ? ('solid' as const) : ('text' as const),
        size: action.isCTA
          ? ({ _: 'fluid', md: 'default' } as const)
          : undefined,
      };

      return (
        <ConditionalWrap
          key={action.label}
          condition={!!action.tooltipContent}
          wrapper={(children) => (
            <Tooltip
              portal
              content={action.tooltipContent}
              arrowProps={{
                className: 'fill-gray',
              }}
            >
              {children}
            </Tooltip>
          )}
        >
          {action.href ? (
            <Link
              {...commonProps}
              href={action.href}
              onClick={(e) => e.stopPropagation()}
            >
              {action.label}
            </Link>
          ) : (
            <Button
              {...commonProps}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                action?.onClick?.();
              }}
            >
              {action.label}
            </Button>
          )}
        </ConditionalWrap>
      );
    })}
  </div>
);

type TourCardProps = {
  homeUniqueId: string;
  imageUrl: string;
  topContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  rounded?: boolean;
};

export const TourCard = ({
  homeUniqueId,
  imageUrl,
  topContent,
  bottomContent,
  rounded,
}: TourCardProps) => {
  return (
    <LinkBase href={`${BASE_PATHS.HOME}/${homeUniqueId}`}>
      <div
        className={cx(styles.container)}
        style={rounded ? { borderRadius: '8px' } : {}}
      >
        <div className="relative flex flex-col md:flex-row">
          <div
            className={cx(styles.homeImage, 'md:w-7xl flex-shrink-0')}
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
          <div className={cx(styles.possibleGrid)}>{topContent}</div>
        </div>
        {bottomContent}
      </div>
    </LinkBase>
  );
};
